import { ReactElement, ReactNode, useEffect, useState } from 'react';

// SVG progress ring driven by a 0–1 fraction. Used for the chat credit marker and
// the billing credit tracker (which overlays a centre label via children).
// With `animate`, the ring draws in from empty on mount and eases to new values.
export const CircularProgress = ({
  fraction,
  size = 36,
  strokeWidth = 4,
  colour = '#55C78E',
  trackColour = 'rgba(128, 128, 128, 0.25)',
  title,
  animate = false,
  children,
}: {
  fraction: number;
  size?: number;
  strokeWidth?: number;
  colour?: string;
  trackColour?: string;
  title?: string;
  animate?: boolean;
  children?: ReactNode;
}): ReactElement => {
  const [displayFraction, setDisplayFraction] = useState(
    animate ? 0 : fraction
  );

  useEffect(() => {
    if (!animate) {
      setDisplayFraction(fraction);

      return;
    }

    const id = requestAnimationFrame(() => setDisplayFraction(fraction));

    return () => cancelAnimationFrame(id);
  }, [animate, fraction]);

  const clamped = Math.min(1, Math.max(0, displayFraction));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped);
  const center = size / 2;

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      title={title}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColour}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={colour}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: `stroke-dashoffset ${
              animate ? '0.8s' : '0.3s'
            } ease`,
          }}
        />
      </svg>
      {children && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          {children}
        </div>
      )}
    </div>
  );
};
